import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Grid,
  Typography,
  Button,
  TextField,
  Tooltip,
  InputAdornment,
  Box
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettingUtils';

const PerformanceSettings = forwardRef((props, ref) => {
  const { 
    isModified, 
    setIsModified, 
    t,
    openSnackbar
  } = useGlobalSettingState();

  // Performance tab keeps non-model performance controls
  const modelMaxContextDefault = 128000;

  const [performanceConfig, setPerformanceConfig] = useState({
    widget_aggregation_max_context_words: modelMaxContextDefault,
    widget_agent_max_context_words: modelMaxContextDefault,
    json_retry_attempts: 1
  });

  const normalizePerformanceData = (data) => {
    if (!data || typeof data !== 'object') {
      return {};
    }
    const normalized = { ...data };
    if (
      normalized.widget_aggregation_max_context_words == null &&
      normalized.widget_aggregation_max_context_chars != null
    ) {
      normalized.widget_aggregation_max_context_words =
        normalized.widget_aggregation_max_context_chars;
    }
    if (
      normalized.widget_agent_max_context_words == null &&
      normalized.widget_agent_max_context_chars != null
    ) {
      normalized.widget_agent_max_context_words =
        normalized.widget_agent_max_context_chars;
    }
    delete normalized.widget_aggregation_max_context_chars;
    delete normalized.widget_agent_max_context_chars;
    return normalized;
  };
  
  // Define handlers for WebSocket messages
  const commandHandlers = useCallback({
    'update_configuration': (message) => {
      if (message.data === 'success') {
        openSnackbar(t('global_setting.save_completed'), 3000);
        setIsModified(false);
      }
    },
    'configuration_data': (message) => {
      setPerformanceConfig((prev) => ({
        ...prev,
        ...normalizePerformanceData(message.data)
      }));
    },
    'performance_settings': (message) => {
      setPerformanceConfig((prev) => ({
        ...prev,
        ...normalizePerformanceData(message.data)
      }));
    }
  }, [openSnackbar, t, setIsModified]);
  
  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);
  
  // Expose getPerformanceSettings method to parent component
  useImperativeHandle(ref, () => ({
    getPerformanceSettings: () => {
      return performanceConfig;
    }
  }));

  // Load settings when component mounts
  useEffect(() => {
    sendCommand('get_performance_settings');
  }, [sendCommand]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setPerformanceConfig((prev) => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const handleSave = () => {
    if (isModified) {
      const payload = {
        widget_aggregation_max_context_words: performanceConfig.widget_aggregation_max_context_words,
        widget_agent_max_context_words: performanceConfig.widget_agent_max_context_words,
        json_retry_attempts: performanceConfig.json_retry_attempts
      };
      // Send only performance settings to the server
      sendCommand('update_configuration', payload);
      
      console.log('Performance settings sent:', payload);
    } else {
      console.error('No changes were made.');
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" gutterBottom>
            {t('global_setting.performance_section')}
          </Typography>
          <Tooltip
            title={t('global_setting.performance_section_tip', 'Tune memory budgets and retries for widgets. Higher budgets keep more evidence; lower budgets speed things up.')}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} />
          </Tooltip>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
          {t('global_setting.widget_aggregation_title', 'Final answer assembly')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('global_setting.widget_aggregation_subtitle', 'Word budget when stitching all evidences into the final widget answer.')}
        </Typography>
        <TextField
          label={t('global_setting.widget_aggregation_max_context_words', 'Final answer max context (words)')}
          value={performanceConfig.widget_aggregation_max_context_words ?? modelMaxContextDefault}
          onChange={handleChange('widget_aggregation_max_context_words')}
          fullWidth
          type="number"
          inputProps={{ min: 1 }}
          helperText={t('global_setting.widget_aggregation_max_context_helper', 'Word budget for building the final widget answer; higher values reduce aggressive evidence merging')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={t(
                    'global_setting.widget_aggregation_max_context_tooltip',
                    'Word limit applied only when assembling the final widget answer from evidences. If set low, evidences are merged/summarized more aggressively before the final call.'
                  )}
                  placement="top"
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                </Tooltip>
              </InputAdornment>
            )
          }}
          sx={{ mt: 1.5 }}
        />

        <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 600 }}>
          {t('global_setting.widget_agent_title', 'Evidence gathering')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('global_setting.widget_agent_subtitle', 'Word budget while collecting and preparing evidence before the final aggregation.')}
        </Typography>
        <TextField
          label={t('global_setting.widget_agent_max_context_words', 'Evidence max context (words)')}
          value={performanceConfig.widget_agent_max_context_words ?? modelMaxContextDefault}
          onChange={handleChange('widget_agent_max_context_words')}
          fullWidth
          type="number"
          inputProps={{ min: 1 }}
          helperText={t('global_setting.widget_agent_max_context_helper', 'Optional word budget; defaults to AI model context if empty')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={t(
                    'global_setting.widget_agent_max_context_tooltip',
                    'Word budget for evidence gathering/processing per widget. Lower values force earlier summarization; higher values keep more raw excerpts before consolidation.'
                  )}
                  placement="top"
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                </Tooltip>
              </InputAdornment>
            )
          }}
          sx={{ mt: 2 }}
        />

        <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 600 }}>
          {t('global_setting.retries_title', 'Resilience')}
        </Typography>
        <TextField
          label={t('global_setting.json_retry_attempts', 'JSON retry attempts (text)')}
          value={performanceConfig.json_retry_attempts || ''}
          onChange={handleChange('json_retry_attempts')}
          fullWidth
          type="number"
          inputProps={{ min: 0 }}
          helperText={t('global_setting.json_retry_attempts_helper', 'Number of extra attempts when JSON parse fails but output looks like JSON')}
          sx={{ mt: 1.5 }}
        />
      </Grid>
      
      <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!isModified}
          sx={{
            backgroundColor: '#5757b0',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#aaaaca',
            },
            py: 1,
            px: 3,
            borderRadius: 2,
          }}
        >
          {t('global_setting.save_button') || 'Save Performance Settings'}
        </Button>
      </Grid>
    </Grid>
  );
});

export { PerformanceSettings };
